import React, { useEffect, useState } from "react";
import { Form, Progress, Button, message, Upload } from "antd";
import { green, red } from "@ant-design/colors";
import { UploadOutlined } from "@ant-design/icons";
import { toast } from "sonner";
import { copyObjects } from "@/util/copyObjects";
import type { UploadProps, RcFile, UploadFile } from "antd/es/upload/interface";
import { customUploadRequest } from "@/util/axiosUpload"; 

interface LegalDocumentsProps {
  onFormFinish?: (values: ICreateResponderCompanyDto) => void;
  initialValues?: any;
  onNext?: () => void;
  onPrevious?: () => void;
  isLastStep?: boolean;
}

const LegalDocuments = ({
  initialValues,
  onNext,
  onPrevious,
  isLastStep,
  onFormFinish,
}: LegalDocumentsProps) => {
  const [form] = Form.useForm();
  const [psiraFileList, setPsiraFileList] = useState<UploadFile[]>([]);
  const [cipcFileList, setCipcFileList] = useState<UploadFile[]>([]);
  const [taxFileList, setTaxFileList] = useState<UploadFile[]>([]);

  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      console.log("LegalDocuments initialValues:", initialValues);
      form.setFieldsValue({
        psiraUrl: initialValues.psiraUrl,
        cipcUrl: initialValues.cipcUrl,
        taxClearanceUrl: initialValues.taxClearanceUrl,
      });

      const createFileListItem = (
        url: string | undefined,
        defaultName: string,
      ): UploadFile[] => {
        if (url) {
          let fileName = url.split("/").pop() || defaultName;

          // Truncate filename if longer than 20 characters
          if (fileName.length > 20) {
            fileName = fileName.substring(0, 20) + "qwertyuiopasdfg... .pdf";
          }

          return [
            {
              uid: url,
              name: fileName,
              status: "done",
              url: url,
            },
          ];
        }
        return [];
      };

      setPsiraFileList(
        createFileListItem(initialValues.psiraUrl, "psira_document.pdf"),
      );
      setCipcFileList(
        createFileListItem(initialValues.cipcUrl, "cipc_document.pdf"),
      );
      setTaxFileList(
        createFileListItem(initialValues.taxClearanceUrl, "tax_clearance.pdf"),
      );
    } else {
      form.resetFields();
      setPsiraFileList([]);
      setCipcFileList([]);
      setTaxFileList([]);
    }
  }, [initialValues, form]);

  const onFinish = (values: ICreateResponderCompanyDto) => {
    toast.success("Saved successfully");

    if (onFormFinish) {
      onFormFinish(values);
    }
    form.resetFields();
  };

  // Normalize Upload onChange event to extract the URL string for form storage.
  // Without this, Form.Item stores the raw upload event object instead of a string,
  // which causes "The JSON value could not be converted to System.String" on the API.
  const normUploadEvent = (e: any): string | undefined => {
    if (typeof e === 'string') return e;
    if (e?.file?.response?.path) return e.file.response.path;
    return undefined;
  };

  const beforeUpload = (file: RcFile) => {
    const isPdf = ["application/pdf"].includes(file.type);
    const isLt2M = file.size / 1024 / 1024 < 10;
    if (!isPdf) {
      toast.error("You can only upload PDF file!");
      return false; // This prevents the file from being added to the list
    }
    if (!isLt2M) {
      toast.error("File must smaller than 10MB!");
      return false; // This prevents the file from being added to the list
    }
    return isPdf && isLt2M;
  };

  const customUpload = async (options: any) => {
    const { onSuccess, onError, file, onProgress } = options;

    // Truncate filename if longer than 20 characters
    let fileToUpload = file;
    if (file.name.length > 20) {
      const truncatedName = file.name.substring(0, 20) + "qwertyuiopasdfg... .pdf";
      fileToUpload = new File([file], truncatedName, { type: file.type });
    }

    const formData = new FormData();
    formData.append("file", fileToUpload);

    try {
      const response = await API.post("/util/post/document", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (event) => {
          if (event.total) {
            const percent = Math.floor((event.loaded / event.total) * 100);
            onProgress({ percent });
          }
        },
      });

      onSuccess(response.data, file);
    } catch (error) {
      console.error("Upload error:", error);
      onError(error);
    }
  };

  // Create a custom upload props generator for each field
  const createUploadProps = (
    fieldName: string,
    currentFileList: UploadFile[],
    setFileListState: React.Dispatch<React.SetStateAction<UploadFile[]>>,
  ): UploadProps => ({
    name: "file",
    customRequest: customUploadRequest,
    beforeUpload: beforeUpload, // Centralized beforeUpload
    accept: ".pdf",
    fileList: currentFileList,
    maxCount: 1,
    onChange: createUploadHandler(fieldName, setFileListState),
    onRemove: () => {
      // Handle file removal
      form.setFieldsValue({ [fieldName]: undefined });
      setFileListState([]);
      return true;
    },
  });

  const createUploadHandler = (
    fieldName: string,
    setFileListState: React.Dispatch<React.SetStateAction<UploadFile[]>>,
  ) => {
    return (info: any) => {
      // info is UploadChangeParam<UploadFile<any>>
      let fileList = [...info.fileList];
      fileList = fileList.slice(-1); // Max 1 file

      // Truncate filename if longer than 20 characters for display
      fileList = fileList.map((file) => {
        if (file.name && file.name.length > 20) {
          return {
            ...file,
            name: file.name.substring(0, 20) + "qwertyuiopasdfg... .pdf",
          };
        }
        return file;
      });

      setFileListState(fileList);

      if (info.file.status === "done") {
        toast.success(`${info.file.name} file uploaded successfully`);
        form.setFieldsValue({ [fieldName]: info.file.response.path });
      } else if (info.file.status === "error") {
        toast.error(`${info.file.name} file upload failed.`);
        form.setFieldsValue({ [fieldName]: undefined }); // Clear form value
        setFileListState([]); // Clear file list display on error
      }
    };
  };

  return (
    <div className="flex w-full h-full">
      <Form form={form} onFinish={onFinish} layout="vertical">
        <div className="flex flex-col w-full">
          <p className="text-sm">Step 2: Legal Documentation</p>
          <Progress
            steps={6}
            size={[80, 10]}
            strokeColor={["#1757A2", "#1757A2"]}
            percent={34}
            showInfo={false}
            className="rounded-full"
            strokeLinecap="round"
            strokeWidth={1}
            style={{
              height: "6px",
              borderRadius: "999px",
            }}
          />
        </div>
        <div className="flex flex-col w-full mt-3">
          <p className="text-sm">PSIRA Registration Ceritificate</p>
          <Form.Item
          name="psiraUrl"
          getValueFromEvent={normUploadEvent}
          rules={[{ required: true, message: "Please attach the PSIRA Registration Ceritificate." }]}
          >
            <Upload
              {...createUploadProps(
                "psiraUrl",
                psiraFileList,
                setPsiraFileList,
              )}
            >
              <Button
                icon={<UploadOutlined />}
                className="bg-saferCity-primary text-popupColor"
              >
                {psiraFileList.length > 0
                  ? "Change PSIRA Doc"
                  : "Upload PSIRA Doc"}
              </Button>
            </Upload>
          </Form.Item>
        </div>

        <div className="flex flex-col w-full">
          <p className="text-sm">CIPC Document</p>
          <Form.Item
          name="cipcUrl"
          getValueFromEvent={normUploadEvent}
          rules={[{ required: true, message: "Please attach the CIPC Document." }]}
          >
            <Upload
              {...createUploadProps("cipcUrl", cipcFileList, setCipcFileList)}
            >
              <Button
                icon={<UploadOutlined />}
                className="bg-saferCity-primary text-popupColor"
              >
                {cipcFileList.length > 0
                  ? "Change CIPC Doc"
                  : "Upload CIPC Doc"}
              </Button>
            </Upload>
          </Form.Item>
        </div>

        <div className="flex flex-col w-full">
          <p className="text-sm">Tax Clearance Certificate</p>
          <Form.Item
          name="taxClearanceUrl"
          getValueFromEvent={normUploadEvent}
          rules={[{ required: true, message: "Please attach the Tax Clearance Certificate." }]}
          >
            <Upload
              {...createUploadProps(
                "taxClearanceUrl",
                taxFileList,
                setTaxFileList,
              )}
            >
              <Button
                icon={<UploadOutlined />}
                className="bg-saferCity-primary text-popupColor"
              >
                {taxFileList.length > 0 ? "Change Tax Doc" : "Upload Tax Doc"}
              </Button>
            </Upload>
          </Form.Item>
        </div>

        <div className="flex w-full">
          <Form.Item className="w-12 pt-2 mb-1">
            <div className="flex w-full gap-2 col-12 justify-content-end align-content-center">
              <Button
                type="primary"
                ghost
                onClick={onPrevious}
                size="large"
                className="w-3"
              >
                Back
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                className="w-3 bg-[#1757A2]"
              >
                Next
              </Button>
            </div>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default LegalDocuments;
